import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Star, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useCart } from '../context/CartContext';
import ProductCarousel from '../components/ProductCarousel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const { addToCart, cartItems } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchProducts is stable, only run on mount

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch products:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
  };

  const getProductQuantityInCart = (productId) => {
    const item = cartItems.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  const filteredProducts = selectedFilter === 'all' 
    ? products 
    : products.filter(p => p.type === selectedFilter);

  const productTypes = ['all', ...new Set(products.map(p => p.type))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <header className="sticky top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-orange-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
            
            <h1 className="text-2xl font-bold text-gray-900">
              <span className="text-orange-500">🔥</span> Roastey Snacks
            </h1>
            
            <Button 
              onClick={() => navigate('/cart')}
              className="bg-orange-500 hover:bg-orange-600 relative"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartItems.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItems.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Page Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our Premium Collection
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Discover our range of healthy, delicious snacks made with premium ingredients
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          {productTypes.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedFilter(type)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                selectedFilter === type
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-orange-100 border border-gray-200'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {filteredProducts.map((product) => (
            <Card 
              key={product.id}
              className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-orange-500 overflow-hidden"
            >
              <CardContent className="p-0">
                {/* Product Image */}
                <div className="relative h-64 overflow-hidden bg-gradient-to-br from-orange-50 to-yellow-50">
                  {product.badge && (
                    <Badge className="absolute top-3 left-3 z-10 bg-orange-500 text-white">
                      {product.badge}
                    </Badge>
                  )}
                  
                  <ProductCarousel images={product.images} />
                  
                  <Button
                    onClick={() => navigate(`/product/${product.id}`)}
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white text-orange-600 hover:bg-orange-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    size="sm"
                  >
                    Quick View
                  </Button>
                </div>

                {/* Product Info */}
                <div className="p-4 space-y-3">
                  <h3 className="font-bold text-lg text-gray-900 line-clamp-2 min-h-[3.5rem]">
                    {product.name}
                  </h3>
                  
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {product.description}
                  </p>

                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${product.id}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {/* Nutrition Highlights */}
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(product.nutrition).slice(0, 2).map(([key, value]) => (
                      <Badge 
                        key={key}
                        variant="outline" 
                        className="text-xs bg-green-50 text-green-700 border-green-200"
                      >
                        {key}: {value}
                      </Badge>
                    ))}
                  </div>

                  {/* Price & Actions */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-orange-600">
                          ₹{product.price}
                        </span>
                        {product.originalPrice && (
                          <span className="text-sm text-gray-400 line-through">
                            ₹{product.originalPrice}
                          </span>
                        )}
                      </div>
                      {product.originalPrice && (
                        <span className="text-xs text-green-600 font-medium">
                          Save {Math.round((1 - product.price / product.originalPrice) * 100)}%
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button 
                      onClick={() => navigate(`/product/${product.id}`)}
                      variant="outline"
                      className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50"
                    >
                      View Details
                    </Button>
                    <Button 
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <ShoppingCart className="mr-2 w-4 h-4" />
                      {getProductQuantityInCart(product.id) > 0 ? (
                        `Added (${getProductQuantityInCart(product.id)})`
                      ) : (
                        'Add to Cart'
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No products found in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
